import React, { useState, useMemo, useEffect, useCallback } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useSelector, shallowEqual } from "react-redux";

// Components
import DataSourceCard from "new_components/molecules/DataSourceCard";
import DataSourcePills from "./DataSourcePills";
import Filters from "./Filters";
import Text from "new_components/atoms/Text";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import { useFilterContext } from "Providers/FilterProvider";
import { useCopilotDataContext } from "../Data/CopilotDataContextV2";
import { useFeedbackContext } from "../Data/FeedbackDataContext";
import { filterStaplesOptions } from "utilities/organizationUtils";

// Hooks
import { usePageTranslations } from "hooks/usePageTranslations";
import { useLanguage } from "hooks/useLanguage";
import { transformCategoriesToFrench } from "utilities/outcomeQTranslations";

// Images
import CloseImg from "assets/images/nav/close.svg";
import CheckImg from "assets/images/nav/check.svg";

// Constants
import colors from "styles/colors";
import typography from "styles/typography";

const extractNumericId = (value) => {
  if (value == null) return null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const id = extractNumericId(item);
      if (id != null) return id;
    }
    return null;
  }
  if (typeof value === "object") {
    if ("id" in value) return extractNumericId(value.id);
    if ("pk" in value) return extractNumericId(value.pk);
    if ("value" in value) return extractNumericId(value.value);
    return null;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return numeric;
  }
  return null;
};

const pickFirstNumericId = (...candidates) => {
  for (const candidate of candidates) {
    const id = extractNumericId(candidate);
    if (id != null) return id;
  }
  return null;
};

const extractDisplayName = (value) => {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const name = extractDisplayName(item);
      if (name) return name;
    }
    return null;
  }
  if (typeof value === "object") {
    const candidates = [
      value.title,
      value.name,
      value.label,
      value.groupTitle,
      value.group_name,
      value.groupName,
      value.team_name,
      value.display_name,
      value.displayName,
    ];
    for (const candidate of candidates) {
      const name = extractDisplayName(candidate);
      if (name) return name;
    }
  }
  return null;
};

const pickFirstDisplayName = (...candidates) => {
  for (const candidate of candidates) {
    const name = extractDisplayName(candidate);
    if (name) return name;
  }
  return null;
};

const formatDisplayDate = (value, t) => {
  if (!value) return t ? t('nA') : "N/A";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return typeof value === "string" ? value : (t ? t('nA') : "N/A");
  }
  return date.toLocaleDateString();
};

// DEFAULT_SURVEY_LOCKED_TOOLTIP will be translated in component

const SURVEY_TYPE_ALIASES = new Set([
  "survey",
  "latest survey",
  "individual",
  "debrief",
  "debriefmulti",
  "combined survey",
  "customdataset",
  "custom dataset",
]);

const normalizeSourceType = (type) => {
  if (type == null) return "";
  const normalized = type.toString().toLowerCase();
  return SURVEY_TYPE_ALIASES.has(normalized) ? "survey" : normalized;
};

const extractSourceId = (item) => {
  if (!item) return null;
  const candidates = [
    item.id,
    item.value?.id,
    item.source?.id,
    item.source?.value?.id,
  ];
  const found = candidates.find(
    (candidate) => candidate !== undefined && candidate !== null
  );
  return found !== undefined && found !== null ? String(found) : null;
};

const normalizeSourceId = (id) => {
  if (id == null) return null;
  const str = String(id);
  return str.startsWith("debrief-") ? str.slice("debrief-".length) : str;
};

// Modal Overlay
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  pointer-events: ${({ isOpen }) => (isOpen ? "auto" : "none")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Modal Container
const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: min(95vw, 900px);
  max-height: 90vh;
  z-index: 2000;
  transform: scale(${({ isOpen }) => (isOpen ? "1" : "0.9")});
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  margin: 20px;
  position: relative;
`;

// Modal Header
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: white;
  border-radius: 12px 12px 0 0;
  flex-shrink: 0;
`;

// Modal Body
const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  display: flex;
  flex-direction: column;
  min-height: 400px;

  /* Custom Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.neutral[200]};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.neutral[500]};
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.neutral[600]};
  }
`;

// Modal Footer
const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 32px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: white;
  border-radius: 0 0 12px 12px;
  flex-shrink: 0;
`;

// Action Buttons
const ActionButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${({ variant }) =>
    variant === "primary"
      ? `
    background: ${colors.primary[500]};
    color: white;
    
    &:hover {
      background: ${colors.primary[600]};
    }
    
    &:disabled {
      background: ${colors.neutral[500]};
      cursor: not-allowed;
    }
  `
      : `
    background: ${colors.neutral[200]};
    color: ${colors.neutral[600]};
    border: 1px solid ${colors.neutral[200]};
    
    &:hover {
      background: ${colors.neutral[500]};
    }
  `}
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

// Tab Components
const TabContainer = styled.div`
  display: flex;
  border-bottom: 2px solid ${colors.neutral[200]};
  background: white;
  padding: 0 32px;
`;

const TabButton = styled.button`
  background: none;
  border: none;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.active ? colors.primary[500] : colors.neutral[600]};
  border-bottom: 2px solid
    ${(props) => (props.active ? colors.primary[500] : "transparent")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${colors.primary[500]};
  }
`;

// Search Container
const SearchContainer = styled.div`
  padding: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  font-size: 14px;
  color: ${colors.neutral[900]};
  background: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 3px ${colors.primary[500]}10;
  }

  &::placeholder {
    color: ${colors.neutral[500]};
  }
`;

// Table Components
const TableContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0 32px 32px 32px;
`;

const TableHeader = styled.div`
  display: flex;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e7ff;
  padding: 0;
`;

const HeaderCell = styled.div`
  padding: 16px 20px;
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #414141;
  letter-spacing: 0.2px;
  width: ${(props) => props.width};
  flex-shrink: 0;
  white-space: nowrap;
`;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #ffffff;
`;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid #eef2ff;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
  background: ${(props) => (props.isSelected ? "#eef2ff" : "#ffffff")};

  &:hover {
    background: #f1f5ff;
    transform: translateX(2px);
    box-shadow: 0 6px 12px rgba(71, 109, 250, 0.08);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DataCell = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: ${(props) => props.width};
  flex-shrink: 0;
  min-width: 0;
`;

const DataSourceName = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  line-height: 1.3;
`;

const DataValue = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  line-height: 1.4;
`;

const ProgressInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProgressText = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
`;

const ProgressBar = styled.div`
  position: relative;
  height: 6px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  width: ${(props) => Math.min(Math.max(props.percentage, 0), 100)}%;
  background: #476dfa;
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease;
`;

const PrimaryActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ iconOnly }) => (iconOnly ? "0" : "8px")};
  padding: ${({ iconOnly }) => (iconOnly ? "8px" : "8px 16px")};
  border-radius: 8px;
  border: none;
  background: ${({ variant, iconOnly }) =>
    iconOnly
      ? colors.neutral[200]
      : variant === "danger"
      ? "#ef4444"
      : "#476dfa"};
  color: ${({ variant, iconOnly }) =>
    iconOnly
      ? variant === "danger"
        ? colors.neutral[600]
        : colors.neutral[600]
      : "#ffffff"};
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease, color 0.2s ease;
  width: ${({ iconOnly }) => (iconOnly ? "36px" : "auto")};
  height: ${({ iconOnly }) => (iconOnly ? "36px" : "auto")};
  vertical-align: middle;

  svg {
    pointer-events: none;
  }

  &:hover {
    background: ${({ variant, iconOnly }) =>
      iconOnly
        ? variant === "danger"
          ? colors.auxiliary.red?.[100] || "#FCDCE0"
          : colors.neutral[500]
        : variant === "danger"
        ? "#dc2626"
        : "#3654c9"};
    color: ${({ variant, iconOnly }) =>
      iconOnly && variant === "danger"
        ? colors.auxiliary.red?.[500] || "#F15062"
        : iconOnly
        ? colors.neutral[600]
        : "#ffffff"};
    transform: ${({ iconOnly }) =>
      iconOnly ? "translateY(-1px) scale(1.05)" : "translateY(-1px)"};
    box-shadow: ${({ iconOnly }) =>
      iconOnly ? "0 4px 12px rgba(0, 0, 0, 0.12)" : "none"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const DisabledActionWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  // justify-content: center;
  cursor: not-allowed;

  button {
    pointer-events: none;
  }
`;

const DisabledIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  padding: 8px;
  background: ${colors.neutral[200]};
  color: ${colors.neutral[600]};
  cursor: not-allowed;
  pointer-events: none;
  vertical-align: middle;

  svg {
    pointer-events: none;
  }
`;

const DataBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: ${(props) => props.color}20;
  color: ${(props) => props.color};
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  width: fit-content;
  transition: all 0.2s ease;
  font-family: "Poppins", sans-serif;

  &:hover {
    transform: scale(1.05);
  }
`;

const AddedText = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #ecfdf3;
  color: #047857;
  border-radius: 8px;
  font-family: "Poppins", sans-serif;
  font-size: 12px;
  font-weight: 600;
`;

// Pagination Components
const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[200]};
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: ${colors.neutral[600]};
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  padding: 8px 12px;
  border: 1px solid ${colors.neutral[200]};
  background: white;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.neutral[200]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ active }) =>
    active &&
    `
    background: ${colors.primary[500]};
    color: white;
    border-color: ${colors.primary[500]};
    
    &:hover {
      background: ${colors.primary[600]};
    }
  `}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.neutral[600]};
`;

const EmptyTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  margin: 0 0 8px 0;
`;

const EmptySubtitle = styled.p`
  font-size: 14px;
  color: ${colors.neutral[600]};
  margin: 0;
  line-height: 1.5;
`;

const TabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Close Button
const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.neutral[200]};
  }
`;

// Section
const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 16px 0;
  position: relative;
  z-index: 1;
`;

// Section Header
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

// Selected Sources Display
const SelectedSourcesSection = styled.div`
  border-top: 1px solid ${colors.neutral[200]};
  padding-top: 24px;
  margin-top: 16px;
  position: relative;
  z-index: 1;
`;

// FilterTabContent Component
const FilterTabContent = ({ enableFilters, dataSourceConfig, t }) => {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = React.useState("");
  const [expandedCategories, setExpandedCategories] = React.useState(new Set());
  const categoryRefs = React.useRef({});
  const mainContentRef = React.useRef(null);

  // Use FilterProvider context
  const {
    selectedFilters,
    handleSelect: contextHandleSelect,
    clearAllFilters,
  } = useFilterContext();

  // Use FeedbackContext to get selectedFactors and selectedOutcomes
  const { selectedFactors, setSelectedFactors, selectedOutcomes, setSelectedOutcomes } = useFeedbackContext();

  const { core_data, get_employee_permission, get_auth, get_selectedOrg } = useSelector(
    (state) => ({
      core_data: state.audit?.core_data,
      get_employee_permission: state.employee_permission,
      get_auth: state.auth,
      get_selectedOrg: state.selectedOrg,
    }),
    shallowEqual
  );

  // Build permission info for filtering
  const employeeId = get_auth?.employee_id;
  const permissionInfo = React.useMemo(() => ({
    permissionRecords: get_employee_permission?.employee_permission,
    employeeId,
    employee: { account_type: get_auth?.account_type },
  }), [get_employee_permission?.employee_permission, employeeId, get_auth?.account_type]);

  // Build extra categories from dataSourceConfig (Outcome & Culture factors)
  const extraCategories = React.useMemo(() => {
    const out = [];

    const outcomeCfg = dataSourceConfig?.find((c) => c.value === "outcome");
    if (outcomeCfg?.options?.length) {
      out.push({
        id: "outcome",
        name: outcomeCfg.label || (t ? t('outcome') : "Outcome"),
        options: outcomeCfg.options.map((opt) => {
          const val = opt.value || opt;
          const id = val?.id ?? val?.value ?? opt.id ?? opt.label;
          // Use fr_name if available and language is French, otherwise use name/title
          const name = language === 'fr' && val?.fr_name && val.fr_name.length > 0
            ? val.fr_name
            : (opt.label || val?.title || val?.name || String(id));
          // Preserve the full outcome data for use in handleSelect
          return { id, name, outcomeData: val };
        }),
      });
    }

    const cultureCfg = dataSourceConfig?.find((c) => c.value === "culture");
    if (cultureCfg?.options?.length) {
      out.push({
        id: "culture",
        name: cultureCfg.label || (t ? t('engagementSurvey') : "Engagement Survey"),
        options: cultureCfg.options.map((opt) => {
          const val = opt.value || opt;
          const id = val?.id ?? val?.value ?? opt.id ?? opt.label;
          // Use fr_title if available and language is French, otherwise use title/name
          const name = language === 'fr' && val?.fr_title && val.fr_title.length > 0
            ? val.fr_title
            : (language === 'fr' && val?.fr && val.fr.length > 0
              ? val.fr
              : (opt.label || val?.title || val?.name || String(id)));
          // Preserve the full factor data for use in handleSelect
          return { id, name, factorData: val };
        }),
      });
    }

    return out;
  }, [dataSourceConfig, language, t]);

  // Apply filterStaplesOptions to core_data categories
  const baseFilteredCategories = React.useMemo(() => {
    const baseCategories = core_data?.categories?.[0]?.categories || [];
    return filterStaplesOptions(baseCategories, permissionInfo);
  }, [core_data?.categories, permissionInfo]);

  // Transform categories to French if language is set to French
  const filteredCategories = React.useMemo(() => {
    if (language === 'fr' && baseFilteredCategories) {
      return transformCategoriesToFrench(baseFilteredCategories);
    }
    return baseFilteredCategories;
  }, [baseFilteredCategories, language]);

  // Merge filtered categories with extraCategories for rendering
  const mergedCategories = React.useMemo(() => {
    return [...filteredCategories, ...extraCategories];
  }, [filteredCategories, extraCategories]);

  // Build refs for each category
  React.useEffect(() => {
    const refs = {};
    mergedCategories.forEach((cat) => {
      refs[cat.id] = React.createRef();
    });
    categoryRefs.current = refs;
  }, [mergedCategories]);

  // Initialize all categories as expanded on mount
  React.useEffect(() => {
    const allIds = new Set(mergedCategories.map(cat => cat.id));
    setExpandedCategories(allIds);
  }, [mergedCategories]);

  // Toggle function
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleSelect = (item, parent) => {
    if (!enableFilters) return;

    // Handle culture factors (Engagement Survey) separately
    // DON'T call contextHandleSelect for factors - they're handled by FeedbackDataContext
    if (parent?.id === "culture" && item?.factorData) {
      const factorObj = item.factorData;
      // Check if factor already exists
      const exists = selectedFactors?.some((f) => f.id === factorObj.id);
      if (exists) {
        // Remove it if it exists (toggle behavior)
        setSelectedFactors(selectedFactors.filter((f) => f.id !== factorObj.id));
      } else {
        // Add it if it doesn't exist
        setSelectedFactors([...(selectedFactors || []), factorObj]);
      }
      // DON'T call contextHandleSelect - FeedbackDataContext will sync to Redux
      return;
    }

    // Handle outcomes separately - DON'T call contextHandleSelect for outcomes
    if (parent?.id === "outcome" && item?.outcomeData) {
      const outcomeObj = item.outcomeData;
      // Check if outcome already exists
      const exists = selectedOutcomes?.some((o) => o.id === outcomeObj.id);
      if (exists) {
        // Remove it if it exists (toggle behavior)
        setSelectedOutcomes(selectedOutcomes.filter((o) => o.id !== outcomeObj.id));
      } else {
        // Add it if it doesn't exist
        setSelectedOutcomes([...(selectedOutcomes || []), outcomeObj]);
      }
      // DON'T call contextHandleSelect - FeedbackDataContext will sync to Redux
      return;
    }

    // For non-factor, non-outcome items, use the FilterProvider's handleSelect
    contextHandleSelect(item, parent);
  };

  const clearAll = () => {
    if (!enableFilters) return;
    clearAllFilters();
  };

  const handleCategoryClick = (categoryId) => {
    if (!enableFilters) return;
    const ref = categoryRefs.current[categoryId]?.current;
    if (ref && mainContentRef.current) {
      const mainContent = mainContentRef.current;
      const categoryTop = ref.offsetTop - 130;
      mainContent.scrollTo({
        top: categoryTop,
        behavior: "smooth",
      });
    }
  };

  // Intelligent search filter logic
  const getFilteredCategories = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return mergedCategories;
    }
    
    const lowerSearch = searchTerm.toLowerCase();
    
    return mergedCategories
      .map(cat => {
        const categoryMatches = cat.name.toLowerCase().includes(lowerSearch);
        const matchingOptions = cat.options.filter(opt => 
          opt.name.toLowerCase().includes(lowerSearch)
        );
        
        if (categoryMatches || matchingOptions.length > 0) {
          return {
            ...cat,
            options: categoryMatches ? cat.options : matchingOptions,
            hasMatch: true
          };
        }
        
        return null;
      })
      .filter(Boolean);
  }, [mergedCategories, searchTerm]);

  // Auto-expand categories with search matches
  React.useEffect(() => {
    if (searchTerm.trim()) {
      const matchingIds = new Set(getFilteredCategories.map(cat => cat.id));
      setExpandedCategories(matchingIds);
    } else {
      // Reset to all expanded when search is cleared
      const allIds = new Set(mergedCategories.map(cat => cat.id));
      setExpandedCategories(allIds);
    }
  }, [searchTerm, getFilteredCategories, mergedCategories]);

  if (!enableFilters) {
    return (
      <FilterTabEmptyState>
        <EmptyTitle>{t ? t('filtersUnavailable') : "Filters Unavailable"}</EmptyTitle>
        <EmptySubtitle>
          {t ? t('filtersUnavailableDescription') : "Please select survey data to enable filters."}
        </EmptySubtitle>
      </FilterTabEmptyState>
    );
  }

  return (
    <FilterTabContainer>
      <FilterTabHeader>
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder={t ? t('searchFilters') : "Search filters..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        {selectedFilters.length > 0 && (
          <FilterActions>
            <ClearAllButton onClick={clearAll}>
              {t ? t('clearAll') : "Clear all"}
            </ClearAllButton>
          </FilterActions>
        )}
      </FilterTabHeader>

      <FilterTabMain>
        {/* <FilterSidebar>
          <FilterSidebarHeader>Filter by category</FilterSidebarHeader>
          {mergedCategories.map((f) => (
            <FilterCategoryItem
              key={f.id}
              onClick={() => handleCategoryClick(f.id)}
            >
              {f.name}
            </FilterCategoryItem>
          ))}
        </FilterSidebar> */}

        <FilterMainContent ref={mainContentRef}>
          {getFilteredCategories.map((cat) => {
            const isExpanded = expandedCategories.has(cat.id);
            
            return (
              <FilterSection key={cat.id} ref={categoryRefs.current[cat.id]}>
                <AccordionHeader 
                  onClick={() => toggleCategory(cat.id)}
                  isExpanded={isExpanded}
                >
                  <span>{cat.name}</span>
                  <CaretIcon isExpanded={isExpanded}>►</CaretIcon>
                </AccordionHeader>
                
                {isExpanded && (
                  <FilterOptions>
                    {cat.options.map((opt) => {
                      // For culture factors and outcomes, check selectedFactors/selectedOutcomes instead of selectedFilters
                      let isSelected = false;
                      if (cat.id === "culture") {
                        isSelected = selectedFactors?.some(
                          (f) => f.id === opt.id
                        ) || false;
                      } else if (cat.id === "outcome") {
                        isSelected = selectedOutcomes?.some(
                          (o) => o.id === opt.id
                        ) || false;
                      } else {
                        isSelected = selectedFilters.some(
                          (i) => i.id === opt.id && i.parent === cat.id
                        );
                      }
                      
                      return (
                        <FilterOption
                          key={opt.id}
                          selected={isSelected}
                          onClick={() => handleSelect(opt, cat)}
                        >
                          {isSelected && (
                            <FilterCheckIcon src={CheckImg} alt="Selected" />
                          )}
                          {opt.name}
                        </FilterOption>
                      );
                    })}
                  </FilterOptions>
                )}
              </FilterSection>
            );
          })}
        </FilterMainContent>
      </FilterTabMain>
    </FilterTabContainer>
  );
};

const DataSourceModal = ({
  isOpen,
  onClose,
  dataSourceConfig,
  selectedSources,
  setSourceType,
  chatStarted,
  uploadPending,
  fileClearCb,
  feedbackLength,
  handleRemoveAllFactors,
  handleRemoveAllOutcomes,
  handleRemoveAllCustomQuestions,
  enableFilters,
  onClear,
  onApply,
  showingForMissingData,
  onApplyAndStartChat,
}) => {
  const t = usePageTranslations('app/audit/AI/Copilot/components/DataSourceDrawer/translations');
  const [activeTab, setActiveTab] = useState("individual");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedSurveys, setLoadedSurveys] = useState([]);
  const [loadingSurveyId, setLoadingSurveyId] = useState(null);
  const ITEMS_PER_PAGE = {
    individual: 4,
    pulse: 8,
    selected: 8,
    default: 8,
  };

  // Get loadDataSource from CopilotDataContext to properly load survey data
  const { loadDataSource } = useCopilotDataContext() || {};

  // Get selectedSurveys from FeedbackContext to sync loadedSurveys state
  const { selectedSurveys } = useFeedbackContext();

  // Clear loadedSurveys when selectedSurveys is cleared (e.g., after "Start New Chat")
  useEffect(() => {
    if (selectedSurveys.length === 0) {
      setLoadedSurveys([]);
    }
  }, [selectedSurveys]);

  // Also clear loadedSurveys when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setLoadedSurveys([]);
    }
  }, [isOpen]);

  const resolveSourcePrimaryTitle = (source, t) => {
    const candidates = [
      source?.metadata?.surveyName,
      source?.metadata?.groupTitle,
      source?.metadata?.datasetName,
      source?.metadata?.seriesName,
      source?.metadata?.selectionLabel,
      source?.title,
      source?.name,
      source?.questionTitle,
      source?.groupTitle,
      source?.source?.title,
      source?.source?.name,
      source?.responses?.[0]?.question?.name,
      source?.value?.title,
    ];

    for (const candidate of candidates) {
      const name = extractDisplayName(candidate);
      if (!name) continue;
      const normalized = name.trim().toLowerCase();
      if (normalized === "series name" || normalized === "series history") {
        continue;
      }
      return name;
    }

    return t ? t('untitledSource') : "Untitled Source";
  };



  const resolveSourceSecondaryLabel = (source, t) => {
    const primary = resolveSourcePrimaryTitle(source, t);
    const label = pickFirstDisplayName(
      source?.groupTitle,
      source?.metadata?.groupTitle,
      source?.metadata?.organizationGroupName,
      source?.metadata?.datasetName,
      source?.metadata?.seriesName,
      source?.metadata?.selectionLabel,
      source?.subtitle,
      source?.responses?.[0]?.organization_group_name,
      source?.responses?.[0]?.organization_group,
      source?.responses?.[0]?.group,
      source?.responses?.[0]?.team,
      source?.source?.groupTitle
    );

    if (!label) return null;
    if (label.toLowerCase() === primary.toLowerCase()) return null;
    return label;
  };

  const resolveSourceLastUpdated = (source, t) =>
    formatDisplayDate(
      source?.lastUpdated ||
      source?.metadata?.completionDate ||
      source?.metadata?.timestamp,
      t
    );

  const buildSelectedDisplayTitle = (source, t) => {
  const surveyName = pickFirstDisplayName(
    source?.responses?.[0]?.name,
    source?.groupTitle,
    source?.metadata?.surveyName,
    source?.questionTitle,
    source?.title,
    source?.responses?.[0]?.question?.name,
    source?.responses?.[0]?.question?.title,
    source?.name
  );

  const groupName = pickFirstDisplayName(
    source?.datasetMetadata?.groupTitle,
    source?.metadata?.groupTitle,
    source?.groupTitle,
    source?.datasetMetadata?.organizationGroupName,
    source?.metadata?.organizationGroupName,
    source?.responses?.[0]?.organization_group_name,
    source?.responses?.[0]?.organization_group,
    source?.responses?.[0]?.group,
    source?.responses?.[0]?.team
  );

  const isIndividualSelection = Boolean(
    source?.metadata?.isIndividual ||
      source?.metadata?.selectionType === "debrief" ||
      source?.type === "Individual" ||
      source?.type === "debrief" ||
      (Array.isArray(source?.responses) && source.responses.length === 1)
  );

  if (isIndividualSelection) {
    return surveyName || groupName || (t ? t('survey') : "Survey");
  }

  const baseLabel = surveyName || groupName;
  const seriesName = pickFirstDisplayName(
    source?.metadata?.seriesName,
    source?.metadata?.selectionLabel,
    source?.responses?.[0]?.name
  );

  if (baseLabel && seriesName) {
    return `${baseLabel} - ${seriesName}`;
  }
    return baseLabel || seriesName || resolveSourcePrimaryTitle(source, t);
};
  // Note: We don't load data here - we just reference what's already loaded from NewAnalytics

  // Get survey data from Redux (same as NewAnalytics)
  const {
    get_culture_audit_reports,
    get_organizations,
    pulseSurveys,
    get_auth,
    existingRawData,
    debriefSchedules,
    organizationGroups,
  } = useSelector(
    (state) => ({
      get_culture_audit_reports: state.debrief_schedule.culture_audit_reports,
      get_organizations: state.organizations,
      pulseSurveys: state.task_templates.scheduled_tasks?.results,
      get_auth: state.auth,
      existingRawData: state.audit?.raw_data,
      debriefSchedules:
        state.debrief_schedule?.debrief_schedule?.results || [],
      organizationGroups: Array.isArray(
        state.organization_group?.organization_groups
      )
        ? state.organization_group.organization_groups
        : [],
    }),
    shallowEqual
  );
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };


  const handleClear = () => {
    if (handleRemoveAllFactors) handleRemoveAllFactors();
    if (handleRemoveAllOutcomes) handleRemoveAllOutcomes();
    if (handleRemoveAllCustomQuestions) handleRemoveAllCustomQuestions();
    if (onClear) onClear();
  };

  const handleApply = () => {
    if (onApply) onApply();
    onClose();
  };

  // Helper function to add an outcome (missing from context)
  const handleSelectOutcome = (outcome) => {
    const config = dataSourceConfig.find((c) => c.value === "outcome");
    if (config && config.callback) {
      // setSelectedOutcomes expects array without 'type' field (context adds it later)
      // Get current outcomes from selectedSources and add the new one
      const currentOutcomes = selectedSources
        .filter((s) => s.type === "outcome")
        .map((s) => ({ id: s.id, title: s.title, level: s.level })); // Remove type field

      // Add the new outcome if it's not already there
      const exists = currentOutcomes.some((o) => o.id === outcome.id);
      if (!exists) {
        config.callback([...currentOutcomes, outcome]);
      }
    }
  };

  // Helper function to add a factor (missing from context)
  const handleSelectFactor = (factor) => {
    const config = dataSourceConfig.find((c) => c.value === "culture");
    if (config && config.callback) {
      // setSelectedFactors expects array without 'type' field (context adds it later)
      // Get current factors from selectedSources and add the new one
      const currentFactors = selectedSources
        .filter((s) => s.type === "culture")
        .map((s) => ({
          id: s.id,
          title: s.title,
          dimension: s.dimension,
          factor: s.factor,
        })); // Remove type field

      // Add the new factor if it's not already there
      const exists = currentFactors.some((f) => f.id === factor.id);
      if (!exists) {
        config.callback([...currentFactors, factor]);
      }
    }
  };

  // Handle pill selection/deselection
  const handleSourceToggle = (source, section) => {
    if (section.value === "pulse") {
      // For pulse surveys, use the existing callback
      if (section.callback) {
        section.callback(source.id);
      }
    } else if (section.value === "outcome") {
      // For outcomes, check if it's already selected and toggle
      const isSelected = selectedSources.some(
        (selected) =>
          selected.type === "outcome" && selected.id === source.value.id
      );

      if (isSelected) {
        // Use the remove callback from config
        const config = dataSourceConfig.find((c) => c.value === "outcome");
        if (config && config.removeCb) {
          config.removeCb(source.value);
        }
      } else {
        // Use our helper function to add
        handleSelectOutcome(source.value);
      }
    } else if (section.value === "culture") {
      // For factors, check if it's already selected and toggle
      const isSelected = selectedSources.some(
        (selected) =>
          selected.type === "culture" && selected.id === source.value.id
      );

      if (isSelected) {
        // Use the remove callback from config
        const config = dataSourceConfig.find((c) => c.value === "culture");
        if (config && config.removeCb) {
          config.removeCb(source.value);
        }
      } else {
        // Use our helper function to add
        handleSelectFactor(source.value);
      }
    } else if (section.value === "custom") {
      // For custom questions, check if it's already selected and toggle
      const isSelected = selectedSources.some(
        (selected) =>
          selected.type === "custom" && selected.id === source.value.id
      );

      if (isSelected) {
        // Use the remove callback from config
        const config = dataSourceConfig.find((c) => c.value === "custom");
        if (config && config.removeCb) {
          config.removeCb(source.value);
        }
      } else {
        // Use the existing handleSelectCustomQuestion from context
        if (section.callback) {
          section.callback(source.value);
        }
      }
    }
  };

  // Search and pagination logic
  const filterData = (data, searchTerm) => {
    if (!searchTerm.trim()) return data;
    const query = searchTerm.toLowerCase();

    return data.filter((item) => {
      const primary = resolveSourcePrimaryTitle(item, t).toLowerCase();
      const secondaryLabel = resolveSourceSecondaryLabel(item, t);
      const secondary = secondaryLabel ? secondaryLabel.toLowerCase() : "";
      const type =
        typeof item.type === "string" ? item.type.toLowerCase() : "";

      return (
        primary.includes(query) ||
        (secondary && secondary.includes(query)) ||
        (type && type.includes(query))
      );
    });
  };

  // Calculate selected counts - only count actual data sources, NOT filters
  const baseSelectedCount = selectedSources?.filter(
    (source) =>
      source.type === "survey" ||
      source.type === "pulse" ||
      source.type === "file" ||
      source.type === "customDataset"
  )?.length || 0;

  // Check if custom dataset is loaded (from NewAnalytics)
  const { shouldUseCustomDataset = false } = useCopilotDataContext() || {};
  const hasCustomData =
    shouldUseCustomDataset &&
    Array.isArray(existingRawData) &&
    existingRawData?.[0]?.datasetMetadata;

  const customDatasetSource = useMemo(() => {
    if (!hasCustomData) return null;

    const metadata = existingRawData?.[0]?.datasetMetadata || {};

    // Don't create customDatasetSource for drawer selections - they're tracked in selectedSurveys
    if (metadata?.sourceLocation === 'drawer') return null;
    const title =
      pickFirstDisplayName(
        metadata?.surveyName,
        metadata?.groupTitle,
        metadata?.datasetName,
        metadata?.seriesName,
        metadata?.selectionLabel
      ) || "Custom dataset";
    const groupTitle = pickFirstDisplayName(
      metadata?.groupTitle,
      metadata?.organizationGroupName
    );

    const selectionType = metadata?.selectionType;
    const selectedSortOrder = metadata?.selectedSortOrder;
    const sourceKey = metadata?.source;

    let id = metadata?.id || metadata?.datasetId || "custom-dataset";
    let type = "customDataset";

    if (selectionType === "sortOrder" && selectedSortOrder != null) {
      id = `sort-${selectedSortOrder}`;
      type = "survey";
    } else if (selectionType === "debrief") {
      const rawId = metadata?.selectedDebriefId ||
        (typeof sourceKey === "string" ? sourceKey.split("-").pop() : null);
      if (rawId) {
        // Store the raw numeric ID to match survey IDs in the list
        id = rawId.toString().replace(/^debrief-/, '');
        type = "survey";
      }
    }

    const lastUpdatedRaw =
      metadata?.timestamp || metadata?.completionDate || metadata?.updatedAt;
    const lastUpdated =
      lastUpdatedRaw && formatDisplayDate(lastUpdatedRaw) !== "N/A"
        ? formatDisplayDate(lastUpdatedRaw)
        : null;

    return {
      id,
      type,
      title,
      lastUpdated,
      groupTitle,
      metadata,
      isCustomDataset: true,
    };
  }, [hasCustomData, existingRawData]);

  const displaySelectedSources = useMemo(() => {
    // Filter out non-data-source items (filters) from selectedSources
    const dataSourcesOnly = selectedSources.filter(
      (source) =>
        source.type === "survey" ||
        source.type === "pulse" ||
        source.type === "file" ||
        source.type === "customDataset" ||
        // Include surveys that might not have type set but have survey-like properties
        source.metadata?.sourceLocation === 'drawer' ||
        (source.responses && Array.isArray(source.responses) && !source.type)
    );

    if (!customDatasetSource) {
      return dataSourcesOnly;
    }

    const matchIndex = dataSourcesOnly.findIndex(
      (source) =>
        source.id === customDatasetSource.id &&
        source.type === customDatasetSource.type
    );

    if (matchIndex !== -1) {
      const next = [...dataSourcesOnly];
      next[matchIndex] = {
        ...next[matchIndex],
        isCustomDataset: true,
        metadata: {
          ...(next[matchIndex]?.metadata || {}),
          ...customDatasetSource.metadata,
        },
      };
      return next;
    }

    return [...dataSourcesOnly, customDatasetSource];
  }, [customDatasetSource, selectedSources]);

  // Check if we have surveys with response data (check displaySelectedSources after it's computed)
  const hasSurveyData = useMemo(() => {
    return displaySelectedSources.some(
      (s) => (s.type === "survey" || s.metadata?.sourceLocation === 'drawer') &&
        (s.responses?.length > 0 || Array.isArray(s.responses))
    );
  }, [displaySelectedSources]);

  // Count matches displaySelectedSources since we already filtered out non-data-sources above
  const totalSelectedCount = displaySelectedSources.length;
  const hasCustomDataset = Boolean(customDatasetSource);
  const filtersEnabled =
    totalSelectedCount > 0 || hasCustomDataset || hasSurveyData;

  const preselectedSurveyIds = useMemo(() => {
    if (!customDatasetSource) return new Set();
    const ids = new Set();
    const metadata = customDatasetSource.metadata || {};

    const addId = (value) => {
      const numeric = extractNumericId(value);
      if (numeric != null) {
        ids.add(String(numeric));
      }
    };

    const addSortOrder = (value) => {
      const numeric = extractNumericId(value);
      if (numeric != null) {
        ids.add(String(numeric));
        ids.add(`sort-${numeric}`);
      }
    };

    addId(metadata?.debriefScheduleId);
    addId(metadata?.selectedDebriefId);

    if (Array.isArray(metadata?.selectedEventIds)) {
      metadata.selectedEventIds.forEach(addId);
    }

    if (metadata?.selectionType === "debrief") {
      addId(
        metadata?.selectedDebriefId ||
          metadata?.debriefScheduleId ||
          metadata?.selectedSortOrder
      );
    }

    if (metadata?.selectionType === "sortOrder") {
      addSortOrder(metadata?.selectedSortOrder);
    }

    if (metadata?.seriesSelected) {
      addSortOrder(metadata?.selectedSortOrder);
    }

    const sourceKey = metadata?.source;
    if (typeof sourceKey === "string") {
      const parts = sourceKey.split("-");
      const possibleId = parts[parts.length - 1];
      addId(possibleId);
    }

    return ids;
  }, [customDatasetSource]);

  const organizationGroupNameMap = useMemo(() => {
    const map = new Map();
    const groups = Array.isArray(organizationGroups) ? organizationGroups : [];

    groups.forEach((group) => {
      const id = Number(group?.id);
      if (Number.isFinite(id) && id > 0) {
        const name =
          group?.name || group?.title || group?.group_name || group?.team_name;
        if (name) {
          map.set(id, name);
        }
      }
    });

    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];

    responses.forEach((response) => {
      const groupObj = response?.organization_group;
      const id = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );
      if (id == null) return;

      const name = pickFirstDisplayName(
        response?.name,
        groupObj?.name,
        response?.organization_group_name,
        response?.group_name,
        response?.team_name
      );

      if (name) {
        map.set(id, name);
      }
    });

    return map;
  }, [organizationGroups, get_culture_audit_reports?.response]);

  const scheduleMapById = useMemo(() => {
    const schedules = Array.isArray(debriefSchedules) ? debriefSchedules : [];
    return schedules.reduce((map, sched) => {
      const schedId = extractNumericId(sched?.id);
      if (schedId != null) {
        map.set(schedId, sched);
      }
      return map;
    }, new Map());
  }, [debriefSchedules]);

  const scheduleToGroupIdMap = useMemo(() => {
    const map = new Map();
    const schedules = Array.isArray(debriefSchedules) ? debriefSchedules : [];

    schedules.forEach((sched) => {
      const schedId = extractNumericId(sched?.id);
      const groupId = sched?.organization_group;

      if (schedId == null || groupId == null) return;

      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const groupName = groupNameFromMap || sched?.group_name || null;

      map.set(schedId, {
        groupId,
        groupName,
      });
    });

    const responses = Array.isArray(get_culture_audit_reports?.response)
      ? get_culture_audit_reports.response
      : [];

    responses.forEach((response) => {
      const schedId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );

      const groupObj = response?.organization_group;
      const groupId = pickFirstNumericId(
        groupObj?.id,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );
      if (schedId == null || groupId == null) return;

      const existing = map.get(schedId);
      const groupNameFromMap = organizationGroupNameMap.get(groupId);
      const candidateName = pickFirstDisplayName(
        groupNameFromMap,
        existing?.groupName,
        groupObj?.name,
        response?.organization_group_name,
        response?.group_name,
        response?.team_name,
        response?.name
      );

      map.set(schedId, {
        groupId,
        groupName: candidateName,
      });
    });

    return map;
  }, [
    debriefSchedules,
    get_culture_audit_reports?.response,
    organizationGroupNameMap,
  ]);

  const resolveIndividualGroupTitle = useCallback(
    (response, sortOrder) => {
      const responseId = extractNumericId(response?.id);
      const directSchedule =
        responseId != null ? scheduleMapById.get(responseId) : null;

      const fallbackScheduleId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );

      const resolvedDebriefScheduleId =
        directSchedule ? responseId : fallbackScheduleId;

      const scheduleGroupMeta =
        resolvedDebriefScheduleId != null
          ? scheduleToGroupIdMap.get(resolvedDebriefScheduleId)
          : null;

      const scheduleGroupId =
        scheduleGroupMeta?.groupId ??
        (directSchedule
          ? pickFirstNumericId(
              directSchedule?.organization_group,
              directSchedule?.organization_group?.id,
              directSchedule?.organization_group_id,
              directSchedule?.group,
              directSchedule?.group_id
            )
          : null);

      const fallbackGroupId = pickFirstNumericId(
        response?.organization_group,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );

      const organizationGroupId = scheduleGroupId ?? fallbackGroupId ?? null;
      const mappedGroupName =
        organizationGroupId != null
          ? organizationGroupNameMap.get(organizationGroupId)
          : null;
      const organizationNameFromResponse = organizationGroupNameMap.get(
        responseId
      );

      return pickFirstDisplayName(
        response?.name,
        mappedGroupName,
        organizationNameFromResponse?.groupName || organizationNameFromResponse,
        scheduleGroupMeta?.groupName,
        directSchedule?.organization_group,
        directSchedule?.group_name,
        response?.organization_group_name,
        response?.organization_group,
        response?.group,
        response?.team,
        response?.group_name,
        response?.team_name,
        sortOrder ? `Group ${sortOrder}` : "Group"
      );
    },
    [scheduleMapById, scheduleToGroupIdMap, organizationGroupNameMap]
  );

  const findMatchingSchedule = useCallback(
    (survey) => {
      if (!survey?.date) return null;

      const explicitId = pickFirstNumericId(
        survey?.debriefScheduleId,
        survey?.value?.debriefScheduleId
      );
      if (explicitId != null) {
        const explicitSchedule = scheduleMapById.get(explicitId);
        if (explicitSchedule) return explicitSchedule;
      }

      if (!Array.isArray(debriefSchedules) || debriefSchedules.length === 0) {
        return null;
      }

      const eventTime = new Date(survey.date).getTime();
      if (Number.isNaN(eventTime)) return null;

      let bestMatch = null;
      let bestScore = Number.POSITIVE_INFINITY;

      for (const schedule of debriefSchedules) {
        const start = schedule?.scheduled_for
          ? new Date(schedule.scheduled_for).getTime()
          : null;
        const end = schedule?.close_at
          ? new Date(schedule.close_at).getTime()
          : null;

        if (start != null && end != null && eventTime >= start && eventTime <= end) {
          bestMatch = schedule;
          bestScore = -1;
          break;
        }

        let score = Number.POSITIVE_INFINITY;
        if (start != null) score = Math.min(score, Math.abs(eventTime - start));
        if (end != null) score = Math.min(score, Math.abs(eventTime - end));

        if (score < bestScore) {
          bestMatch = schedule;
          bestScore = score;
        }
      }

      return bestMatch;
    },
    [debriefSchedules, scheduleMapById]
  );

  const getParticipantsForSurvey = useCallback(
    (survey) => {
      const primary = Number(survey?.requestedCount) || 0;
      if (primary > 0) return primary;

      const requestList = survey?.responses?.[0]?.request_emp_id;
      if (Array.isArray(requestList) && requestList.length > 0) {
        return requestList.length;
      }

      const schedule = findMatchingSchedule(survey);
      return Number(schedule?.total_requests) || 0;
    },
    [findMatchingSchedule]
  );

  const resolveSelectedBadge = (selectedSource, t) => {
    if (!selectedSource) return null;

    // Check if this was loaded from drawer - if so, treat as regular survey, not custom dataset
    const isFromDrawer = selectedSource?.metadata?.sourceLocation === 'drawer';

    if (selectedSource.isCustomDataset && !isFromDrawer) {
      return {
        label: t ? t('customDataset') : "Custom Dataset",
        color: colors.primary[600],
      };
    }

    const type = normalizeSourceType(selectedSource.type);

    if (type === "survey") {
      const isIndividualSelection = Boolean(
        selectedSource?.metadata?.isIndividual ||
          selectedSource?.metadata?.selectionType === "debrief" ||
          selectedSource?.type === "Individual" ||
          selectedSource?.type === "debrief" ||
          (Array.isArray(selectedSource?.responses) &&
            selectedSource.responses.length === 1)
      );

      if (isIndividualSelection) {
        return {
          label: t ? t('individualSurvey') : "Individual Survey",
          color: colors.secondary[500],
        };
      }

      return {
        label: t ? t('surveySeriesLabel') : "Survey Series",
        color: colors.primary[500],
      };
    }

    if (type === "pulse") {
      return {
        label: t ? t('pulseSurvey') : "Pulse Survey",
        color: colors.secondary[600],
      };
    }

    if (type === "culture") {
      return {
        label: t ? t('cultureFactor') : "Culture Factor",
        color: colors.primary[600],
      };
    }

    if (type === "outcome") {
      return {
        label: t ? t('outcomeLabel') : "Outcome",
        color: colors.secondary[500],
      };
    }

    if (type === "custom") {
      return {
        label: t ? t('customQuestion') : "Custom Question",
        color: colors.auxiliary.yellow[500],
      };
    }

    if (type === "file") {
      return {
        label: t ? t('fileUpload') : "File Upload",
        color: colors.neutral[600],
      };
    }

    return null;
  };

  const findMatchingSelectedSource = useCallback(
    (candidate) => {
      const candidateIdRaw = extractSourceId(candidate);
      if (candidateIdRaw == null) return null;
      const candidateNormalizedId = normalizeSourceId(candidateIdRaw);
      const candidateType = normalizeSourceType(candidate?.type);

      const match = displaySelectedSources.find((selected) => {
        const selectedIdRaw = extractSourceId(selected);
        if (selectedIdRaw == null) return false;
        const selectedNormalizedId = normalizeSourceId(selectedIdRaw);
        const selectedType = normalizeSourceType(selected?.type);

        if (candidateType === "survey" && selectedType === "survey") {
          return (
            selectedIdRaw === candidateIdRaw ||
            selectedNormalizedId === candidateNormalizedId
          );
        }

        return (
          selectedType === candidateType &&
          (selectedIdRaw === candidateIdRaw ||
            selectedNormalizedId === candidateNormalizedId)
        );
      });

      if (match) return match;

      if (
        customDatasetSource &&
        candidateType === "survey" &&
        preselectedSurveyIds.size > 0
      ) {
        const candidateDebriefIds = [
          candidate?.debriefScheduleId,
          candidate?.value?.debriefScheduleId,
          candidate?.source?.debriefScheduleId,
        ];

        const matchesPreselected =
          (candidateNormalizedId &&
            preselectedSurveyIds.has(candidateNormalizedId)) ||
          candidateDebriefIds.some((value) => {
            const numeric = extractNumericId(value);
            if (numeric == null) return false;
            return preselectedSurveyIds.has(String(numeric));
          });

        if (matchesPreselected) {
          return customDatasetSource;
        }
      }

      return null;
    },
    [displaySelectedSources, customDatasetSource, preselectedSurveyIds]
  );

  const isSourceSelected = useCallback(
    (candidate) => {
      return Boolean(findMatchingSelectedSource(candidate));
    },
    [findMatchingSelectedSource]
  );

  const describeSelectionStatus = useCallback((selectedEntry, t) => {
    if (!selectedEntry) {
      return {
        label: t ? t('selectedLabel') : "Selected",
        tooltip: t ? t('selectedTooltip') : "This data source is already part of the chat.",
      };
    }

    const preselected =
      Boolean(selectedEntry?.status === 22) ||
      Boolean(selectedEntry?.metadata?.selectionType) ||
      Boolean(selectedEntry?.metadata?.preselected) ||
      Boolean(selectedEntry?.preselected) ||
      Boolean(selectedEntry?.isDefaultSelection) ||
      Boolean(selectedEntry?.fromExternal) ||
      Boolean(selectedEntry?.source?.preselected);

    if (preselected) {
      return {
        label: t ? t('added') : "Added",
        tooltip: t ? t('preselectedTooltip') : "This survey was pre-selected from another page and is already active.",
      };
    }

    return {
      label: t ? t('added') : "Added",
      tooltip: t ? t('selectedTooltip') : "This data source is already part of the chat.",
    };
  }, []);

  // Process survey data by sort order (same logic as NewAnalytics)
  const surveyDataBySortOrder = useMemo(() => {
    if (!get_culture_audit_reports?.response?.length) return [];

    const sortedResponses = [...get_culture_audit_reports.response].sort(
      (a, b) => b.id - a.id
    );

    // Group by sort order
    const grouped = sortedResponses.reduce((acc, response) => {
      const sortOrder = response.question?.sort_order;
      if (!sortOrder) return acc;

      if (!acc[sortOrder]) {
        acc[sortOrder] = {
          sortOrder,
          questionTitle: response.question?.name || `Survey ${sortOrder}`,
          responses: [],
          latestDate: null,
        };
      }

      acc[sortOrder].responses.push(response);

      // Track latest date
      const responseDate = new Date(response.date);
      if (
        !acc[sortOrder].latestDate ||
        responseDate > acc[sortOrder].latestDate
      ) {
        acc[sortOrder].latestDate = responseDate;
      }

      return acc;
    }, {});

    // Convert to array and get survey counts
    return Object.values(grouped)
      .map((group) => {
        return {
          id: `sort-${group.sortOrder}`,
          title: group.questionTitle,
          type: "Latest Survey",
          status: "Active",
          surveysCount: group.responses?.length || 0,
          lastUpdated: group.latestDate?.toLocaleDateString() || "N/A",
          sortOrder: group.sortOrder,
          responses: group.responses,
          source: {
            id: `sort-${group.sortOrder}`,
            title: group.questionTitle,
            value: {
              id: `sort-${group.sortOrder}`,
              title: group.questionTitle,
              sortOrder: group.sortOrder,
              responses: group.responses,
            },
          },
        };
      })
      .sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
  }, [get_culture_audit_reports]);

  // Process individual survey debriefs (same logic as NewAnalytics)
  const surveyDebriefsByGroup = useMemo(() => {
    if (!get_culture_audit_reports?.response?.length) return [];

    const sortedResponses = [...get_culture_audit_reports.response].sort(
      (a, b) =>
        (extractNumericId(b?.id) || 0) - (extractNumericId(a?.id) || 0)
    );

    return sortedResponses.map((response, index) => {
      const formattedDate = formatDisplayDate(response.date);
      const sortOrder = response.question?.sort_order;
      const questionTitle =
        pickFirstDisplayName(
          response?.question?.name,
          response?.question?.title,
          `Survey ${sortOrder}`
        ) || `Survey ${sortOrder || ""}`.trim();

      const responseId = extractNumericId(response?.id);
      const directSchedule =
        responseId != null ? scheduleMapById.get(responseId) : null;

      const fallbackScheduleId = pickFirstNumericId(
        response?.debrief_schedule,
        response?.debrief_schedule_id,
        response?.schedule,
        response?.schedule_id,
        response?.survey_schedule,
        response?.survey_schedule_id,
        response?.survey_event,
        response?.survey_event_id
      );

      const resolvedDebriefScheduleId =
        directSchedule ? responseId : fallbackScheduleId;

      const scheduleGroupMeta =
        resolvedDebriefScheduleId != null
          ? scheduleToGroupIdMap.get(resolvedDebriefScheduleId)
          : null;

      const scheduleGroupId =
        scheduleGroupMeta?.groupId ??
        (directSchedule
          ? pickFirstNumericId(
              directSchedule?.organization_group,
              directSchedule?.organization_group?.id,
              directSchedule?.organization_group_id,
              directSchedule?.group,
              directSchedule?.group_id
            )
          : null);

      const fallbackGroupId = pickFirstNumericId(
        response?.organization_group,
        response?.organization_group_id,
        response?.group,
        response?.group_id,
        response?.team,
        response?.team_id,
        response?.organization_group_details?.id
      );

      const organizationGroupId = scheduleGroupId ?? fallbackGroupId ?? null;
      const groupTitle = resolveIndividualGroupTitle(response, sortOrder);

      const respondentCount = Array.isArray(response?.responses)
        ? response.responses.length
        : 0;
      const requestedCount = Array.isArray(response?.request_emp_id)
        ? response.request_emp_id.length
        : 0;
      const questionCount = response.questions?.length || 0;

      const historyIdRaw =
        responseId ??
        resolvedDebriefScheduleId ??
        extractNumericId(response?.responses?.[0]?.id) ??
        response?.id ??
        `debrief-${index}`;
      const historyId =
        historyIdRaw != null && historyIdRaw !== ""
          ? String(historyIdRaw)
          : `debrief-${index}`;

      return {
        id: historyId,
        title: questionTitle,
        questionTitle,
        type: "debrief",
        status: "Active",
        lastUpdated: formattedDate,
        date: response.date,
        sortOrder,
        groupTitle,
        organizationGroupId,
        debriefScheduleId: resolvedDebriefScheduleId,
        responses: [response],
        responseCount: 1,
        respondentCount,
        requestedCount,
        questionCount,
        source: {
          id: historyId,
          title: questionTitle,
          value: {
            id: historyId,
            title: questionTitle,
            responses: [response],
            date: response.date,
            sortOrder,
            organizationGroupId,
            debriefScheduleId: resolvedDebriefScheduleId,
          },
        },
      };
    });
  }, [
    get_culture_audit_reports,
    resolveIndividualGroupTitle,
    scheduleMapById,
    scheduleToGroupIdMap,
  ]);

  // Process pulse surveys (filtered by user access)
  const pulseData = useMemo(() => {
    if (!pulseSurveys?.length) return [];

    // Filter pulses based on user access - same logic as FeedbackDataContext
    const userCreatedPulses = pulseSurveys.filter(
      (pulse) => pulse.created_by === get_auth?.employee?.id
    );
    const userAccessedPulses = pulseSurveys.filter((pulse) =>
      pulse.employee_access?.includes(get_auth?.employee?.id)
    );
    const accessiblePulses = [...userCreatedPulses, ...userAccessedPulses];

    return accessiblePulses
      .filter((pulse) => pulse.name && pulse.name.trim() !== "")
      .map((pulse) => {
      const createdDate = new Date(pulse.created_at).toLocaleDateString();
      const responseCount = pulse.task_responses?.length || 0;

      return {
        id: pulse.id,
        title: pulse.name || "Untitled Pulse",
        type: "pulse",
        status: "Active",
        lastUpdated: createdDate,
        responseCount,
        source: {
          id: pulse.id,
          title: pulse.name || "Untitled Pulse",
          value: {
            id: pulse.id,
            title: pulse.name || "Untitled Pulse",
            name: pulse.name,
            responseCount,
            created_at: pulse.created_at,
          },
        },
      };
    });
  }, [pulseSurveys, get_auth]);

  // Get filtered and paginated data for each tab
  const getFilteredAndPaginatedData = (data, tab) => {
    const perPage = ITEMS_PER_PAGE[tab] || ITEMS_PER_PAGE.default;
    const filtered = filterData(data, searchTerm);
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * perPage;
    const paginated = filtered.slice(startIndex, startIndex + perPage);

    return {
      data: paginated,
      totalItems: filtered.length,
      totalPages,
      currentPage: safePage,
      itemsPerPage: perPage,
    };
  };

  // Prepare data for each tab
  const individualPageData = getFilteredAndPaginatedData(
    surveyDebriefsByGroup,
    "individual"
  );
  const pulsePageData = getFilteredAndPaginatedData(pulseData, "pulse");

  // Reset pagination when switching tabs or searching
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const seriesTitleMap = useMemo(() => {
    const map = new Map();
    surveyDataBySortOrder.forEach((series) => {
      if (series?.sortOrder != null) {
        map.set(series.sortOrder, resolveSourcePrimaryTitle(series, t));
      }
    });
    return map;
  }, [surveyDataBySortOrder]);

  // Pagination component
  const PaginationComponent = ({
    totalPages,
    currentPage,
    onPageChange,
    totalItems,
    itemsPerPage,
    t,
  }) => {
    if (totalPages <= 1) return null;

    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return (
      <PaginationContainer>
        <PaginationInfo>
          {t('showingResults', { start: startItem, end: endItem, total: totalItems })}
        </PaginationInfo>
        <PaginationControls>
          <PaginationButton
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {t('previous')}
          </PaginationButton>

          {[...Array(totalPages)].map((_, index) => {
            const page = index + 1;
            // Show first page, last page, current page, and pages around current
            const showPage =
              page === 1 ||
              page === totalPages ||
              Math.abs(page - currentPage) <= 1;

            if (!showPage && page === 2 && currentPage > 4) {
              return <span key={page}>...</span>;
            }
            if (
              !showPage &&
              page === totalPages - 1 &&
              currentPage < totalPages - 3
            ) {
              return <span key={page}>...</span>;
            }
            if (!showPage) return null;

            return (
              <PaginationButton
                key={page}
                active={page === currentPage}
                onClick={() => onPageChange(page)}
              >
                {page}
              </PaginationButton>
            );
          })}

          <PaginationButton
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {t('next')}
          </PaginationButton>
        </PaginationControls>
      </PaginationContainer>
    );
  };

  // Handle adding a source
  const handleAddSource = async (availableSource) => {
    // For survey sources, we need to handle them differently
    if (
      availableSource.type === "Latest Survey" ||
      availableSource.type === "Individual" ||
      availableSource.type === "debrief"
    ) {
      await handleAddSurveySource(availableSource);
    } else if (availableSource.type === "pulse") {
      handleAddPulseSource(availableSource);
    } else {
      // Handle legacy data source config items
      const section = dataSourceConfig.find(
        (c) => c.value === availableSource.type
      );
      if (section) {
        handleSourceToggle(availableSource.source, section);
      }
    }
  };

  // Handle adding survey sources - use loadDataSource to match NewAnalytics behavior
  const handleAddSurveySource = async (surveySource) => {
    // Check if already selected in selectedSurveys from context (source of truth)
    const isAlreadySelected = selectedSurveys.some(
      (s) => s.id === surveySource.id ||
        (s.metadata?.originalDrawerId === surveySource.id)
    );
    if (isAlreadySelected) {
      console.log('Survey already selected in selectedSurveys, skipping:', surveySource.id);
      return;
    }

    // Also check selectedSources as fallback
    const isInSelectedSources = selectedSources.some(
      (s) => s.id === surveySource.id ||
        (s.type === 'survey' && s.metadata?.originalDrawerId === surveySource.id)
    );
    if (isInSelectedSources) {
      console.log('Survey already in selectedSources, skipping:', surveySource.id);
      return;
    }

    // Prevent duplicate loading
    if (loadedSurveys.includes(surveySource.id)) {
      console.log('Survey already being loaded, skipping:', surveySource.id);
      return;
    }

    // Set loading state
    setLoadingSurveyId(surveySource.id);
    setLoadedSurveys((prev) => [...prev, surveySource.id]);
    try {
      const surveyName = pickFirstDisplayName(
        surveySource?.datasetMetadata?.surveyName,
        surveySource?.questionTitle,
        surveySource?.responses?.[0]?.question?.title,
        surveySource?.responses?.[0]?.question?.name,
        surveySource?.title
      );

      const groupName = pickFirstDisplayName(
        surveySource?.datasetMetadata?.groupTitle,
        surveySource?.groupTitle,
        surveySource?.responses?.[0]?.organization_group_name,
        surveySource?.responses?.[0]?.organization_group,
        surveySource?.responses?.[0]?.group,
        surveySource?.responses?.[0]?.team
      );

      // Determine selection type based on survey source type
      const isIndividual = surveySource.type === "Individual" || surveySource.type === "debrief";
      const selectionType = isIndividual ? "debrief" : "sortOrder";

      // Build data source object that matches what NewAnalytics passes to loadDataSource
      const dataSourceForLoad = {
        id: surveySource.id,
        sortOrder: surveySource.sortOrder,
        questionTitle: surveyName || surveySource.questionTitle || surveySource.title,
        title: surveyName || groupName || surveySource.title,
        groupTitle: groupName || surveySource.groupTitle,
        responses: surveySource.responses,
        debriefScheduleId: surveySource.debriefScheduleId,
        organizationGroupId: surveySource.organizationGroupId,
        selectedEventIds: surveySource.selectedEventIds,
      };

      // Track the selection inside FeedbackContext
      // NOTE: We do NOT call loadDataSource for drawer selections to avoid Redux conflicts
      const surveyConfig = dataSourceConfig.find((c) => c.value === "survey");
      if (surveyConfig?.callback) {
        const completionDate =
          surveySource?.metadata?.completionDate ||
          surveySource?.lastUpdated ||
          surveySource?.responses?.[0]?.date ||
          null;

        const normalizedSelection = {
          id: surveySource.id,
          type: "survey",
          title: surveyName || groupName || surveySource.title || "Survey",
          responses: Array.isArray(surveySource.responses)
            ? surveySource.responses
            : [],
          surveysCount:
            surveySource.surveysCount ||
            (Array.isArray(surveySource.responses)
              ? surveySource.responses.length
              : 0),
          lastUpdated: completionDate,
          metadata: {
            ...(surveySource.metadata || {}),
            surveyName: surveyName || surveySource?.metadata?.surveyName,
            groupTitle: groupName || surveySource?.metadata?.groupTitle,
            completionDate,
            selectionType,
            sourceLocation: "drawer",
            originalDrawerId: surveySource.id,
            sortOrder: surveySource.sortOrder,
            isIndividual,
          },
        };

        surveyConfig.callback(normalizedSelection);
      }

      // Use loadDataSource to process survey data (needed for feedback processing)
      // But we prevent it from creating custom dataset entries by checking sourceLocation
      if (loadDataSource) {
        await loadDataSource(dataSourceForLoad, selectionType, 'drawer');
      }
    } catch (error) {
      console.error("Error loading survey data:", error);
    } finally {
      // Clear loading state after a brief delay to show success state
      setTimeout(() => setLoadingSurveyId(null), 300);
    }
  };

  // Handle adding pulse sources
  const handleAddPulseSource = (pulseSource) => {
    // Create a pulse data object similar to how it's handled in CopilotDataContextV2
    const pulseData = {
      id: pulseSource.id,
      title: pulseSource.title,
      type: "pulse",
      name: pulseSource.title,
      responseCount: pulseSource.responseCount,
      lastUpdated: pulseSource.lastUpdated,
      source: pulseSource.source,
    };

    // Find the pulse config section to add this to
    const pulseConfig = dataSourceConfig.find((c) => c.value === "pulse");
    if (pulseConfig && pulseConfig.callback) {
      pulseConfig.callback(pulseData.id); // Based on CopilotDataContextV2, it expects the pulse ID
    } else {
      console.warn("No pulse config found for handling pulse selection");
    }
  };

  // Handle removing a selected source
  const handleRemoveSelected = (selectedSource) => {
    // For survey sources, find the survey config
    if (
      selectedSource.type === "survey" ||
      selectedSource.type === "Latest Survey" ||
      selectedSource.type === "Individual" ||
      selectedSource.type === "debrief"
    ) {
      // If this was loaded from the drawer, remove from loadedSurveys using the original drawer ID
      if (selectedSource?.metadata?.sourceLocation === 'drawer') {
        const originalDrawerId = selectedSource?.metadata?.originalDrawerId || selectedSource.id;
        console.log('Removing from loadedSurveys:', originalDrawerId);
        setLoadedSurveys((prev) => prev.filter((id) => id !== originalDrawerId));
      }

      const surveyConfig = dataSourceConfig.find((c) => c.value === "survey");
      if (surveyConfig && surveyConfig.removeCb) {
        // Pass the survey ID, not the entire object
        surveyConfig.removeCb(selectedSource.id);
      }
    } else if (selectedSource.type === "pulse") {
      // For pulse sources, find the pulse config
      const pulseConfig = dataSourceConfig.find((c) => c.value === "pulse");
      if (pulseConfig && pulseConfig.removeCb) {
        // Pass the pulse ID, not the entire object
        pulseConfig.removeCb(selectedSource.id);
      }
    } else if (selectedSource.isCustomDataset) {
      // Custom datasets originate from NewAnalytics and are managed there.
      return;
    } else {
      // For other source types, use the existing logic
      const section = dataSourceConfig.find(
        (c) => c.value === selectedSource.type
      );
      if (section && section.removeCb) {
        section.removeCb(selectedSource);
      }
    }
  };

  // Don't render anything if drawer is closed
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
        <ModalContainer isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Icon
                name={ICON_SET.database}
                size={20}
                color={colors.primary[500]}
              />
              <Text typographyStyle={typography.body.semibold}>
                {showingForMissingData
                  ? t('pleaseSelectDataSource')
                  : t('surveyData')}
              </Text>
            </div>
            <CloseButton onClick={onClose}>
              <Icon
                name={ICON_SET.clear}
                size={20}
                color={colors.neutral[500]}
              />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            <TabContainer>
              <TabButton
                active={activeTab === "individual"}
                onClick={() => setActiveTab("individual")}
              >
                {t('individualSurveys')}
              </TabButton>
              <TabButton
                active={activeTab === "pulse"}
                onClick={() => setActiveTab("pulse")}
              >
                {t('pulseSurveys')}
              </TabButton>
              <TabButton
                active={activeTab === "selected"}
                onClick={() => setActiveTab("selected")}
              >
                {t('selected')} ({totalSelectedCount})
              </TabButton>
              <TabButton
                active={activeTab === "filters"}
                onClick={() => setActiveTab("filters")}
              >
                {t('filters')}
              </TabButton>
            </TabContainer>

            <TabContent>
              {activeTab === "individual" && (
                <>
                  <SearchContainer>
                    <SearchInput
                      type="text"
                      placeholder={t('searchIndividualSurveys')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </SearchContainer>
                  <TableContainer>
                    <TableHeader>
                      <HeaderCell width="28%">{t('surveyName')}</HeaderCell>
                      <HeaderCell width="22%">{t('surveySeries')}</HeaderCell>
                      <HeaderCell width="15%">{t('lastCloseDate')}</HeaderCell>
                      <HeaderCell width="20%">{t('compliance')}</HeaderCell>
                      <HeaderCell width="15%">{t('actions')}</HeaderCell>
                    </TableHeader>
                    <TableBody>
                      {individualPageData.data.length > 0 ? (
                        individualPageData.data.map((survey, index) => {
                          const matchedSelection =
                            findMatchingSelectedSource(survey);
                          const isSelected = Boolean(matchedSelection);
                          const selectionStatus = isSelected
                            ? describeSelectionStatus(matchedSelection, t)
                            : null;
                          const surveyTitle = resolveSourcePrimaryTitle(survey, t);
                          const seriesTitle =
                            seriesTitleMap.get(survey.sortOrder) ||
                            survey.questionTitle ||
                            surveyTitle;
                          const primaryLabel =
                            pickFirstDisplayName(
                              resolveSourceSecondaryLabel(survey, t),
                              survey.groupTitle,
                              survey.organizationGroupName,
                              surveyTitle
                            ) || surveyTitle;
                          const dateLabel =
                            survey.formattedDate ||
                            formatDisplayDate(survey.lastUpdated, t);
                          const responsesCount =
                            Number(survey.respondentCount) ||
                            Number(survey.responseCount) ||
                            (Array.isArray(survey.responses?.[0]?.responses)
                              ? survey.responses[0].responses.length
                              : 0);
                          const participantsPrimary =
                            Number(survey.requestedCount) || 0;
                          const participantsFallback =
                            getParticipantsForSurvey(survey) || 0;
                          const participants =
                            participantsPrimary > 0
                              ? participantsPrimary
                              : participantsFallback;
                          const hasResponses = responsesCount > 0;
                          const hasParticipantTotal = participants > 0;
                          const compliancePct = hasParticipantTotal
                            ? (responsesCount / participants) * 100
                            : 0;

                          return (
                            <TableRow
                              key={`individual-${survey.id}-${index}`}
                              isSelected={isSelected}
                            >
                              <DataCell width="28%">
                                <DataSourceName>{primaryLabel}</DataSourceName>
                              </DataCell>
                              <DataCell width="22%">
                                <DataValue>{seriesTitle}</DataValue>
                              </DataCell>
                              <DataCell width="15%">
                                <DataValue>{dateLabel}</DataValue>
                              </DataCell>
                              <DataCell width="20%">
                                {hasResponses ? (
                                  hasParticipantTotal ? (
                                    <ProgressInfo>
                                      <ProgressText>
                                        {responsesCount} / {participants} {t('responses')}
                                      </ProgressText>
                                      <ProgressBar>
                                        <ProgressFill percentage={compliancePct} />
                                      </ProgressBar>
                                    </ProgressInfo>
                                  ) : (
                                    <ProgressInfo>
                                      <ProgressText>
                                        {responsesCount}{" "}
                                        {responsesCount === 1
                                            ? t('response')
                                            : t('responses')}
                                      </ProgressText>
                                    </ProgressInfo>
                                  )
                                ) : (
                                    <DataValue>{t('nA')}</DataValue>
                                )}
                              </DataCell>
                              <DataCell width="15%">
                                {loadingSurveyId === survey.id ? (
                                  <PrimaryActionButton disabled>
                                    <Icon
                                      name={ICON_SET.add}
                                      size={14}
                                      color="#ffffff"
                                    />
                                    {t('adding')}
                                  </PrimaryActionButton>
                                ) : isSelected ? (
                                  <AddedText title={selectionStatus?.tooltip}>
                                    <svg viewBox="0 0 24 24" fill="none">
                                      <path
                                        d="M5 13l4 4L19 7"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                      {selectionStatus?.label || t('added')}
                                  </AddedText>
                                ) : (
                                  <PrimaryActionButton
                                    onClick={() => handleAddSource(survey)}
                                  >
                                    <Icon
                                      name={ICON_SET.add}
                                      size={14}
                                      color="#ffffff"
                                    />
                                        {t('add')}
                                  </PrimaryActionButton>
                                )}
                              </DataCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <EmptyState>
                            <EmptyTitle>{t('noIndividualSurveys')}</EmptyTitle>
                          <EmptySubtitle>
                              {t('noIndividualSurveysDescription')}
                          </EmptySubtitle>
                        </EmptyState>
                      )}
                    </TableBody>
                    <PaginationComponent
                      totalPages={individualPageData.totalPages}
                      currentPage={individualPageData.currentPage}
                      onPageChange={setCurrentPage}
                      totalItems={individualPageData.totalItems}
                      itemsPerPage={individualPageData.itemsPerPage}
                      t={t}
                    />
                  </TableContainer>
                </>
              )}
              {activeTab === "pulse" && (
                <>
                  <SearchContainer>
                    <SearchInput
                      type="text"
                      placeholder={t('searchPulseSurveys')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </SearchContainer>
                  <TableContainer>
                    <TableHeader>
                      <HeaderCell width="70%">{t('pulseSurveyName')}</HeaderCell>
                      <HeaderCell width="15%">{t('createdDate')}</HeaderCell>
                      <HeaderCell width="15%">{t('actions')}</HeaderCell>
                    </TableHeader>
                    <TableBody>
                      {pulsePageData.data.length > 0 ? (
                        pulsePageData.data.map((pulse, index) => {
                          const matchedSelection =
                            findMatchingSelectedSource(pulse);
                          const isSelected = Boolean(matchedSelection);
                          const selectionStatus = isSelected
                            ? describeSelectionStatus(matchedSelection, t)
                            : null;
                          const primaryTitle = resolveSourcePrimaryTitle(pulse, t);
                          const createdDate = formatDisplayDate(
                            pulse.lastUpdated,
                            t
                          );

                          return (
                            <TableRow
                              key={`pulse-${pulse.id}-${index}`}
                              isSelected={isSelected}
                            >
                              <DataCell width="70%">
                                <DataSourceName>{primaryTitle}</DataSourceName>
                              </DataCell>
                              <DataCell width="15%">
                                <DataValue>{createdDate}</DataValue>
                              </DataCell>
                              <DataCell width="15%">
                                {loadingSurveyId === pulse.id ? (
                                  <PrimaryActionButton disabled>
                                    <Icon
                                      name={ICON_SET.add}
                                      size={14}
                                      color="#ffffff"
                                    />
                                    {t('adding')}
                                  </PrimaryActionButton>
                                ) : isSelected ? (
                                  <AddedText title={selectionStatus?.tooltip}>
                                    <svg viewBox="0 0 24 24" fill="none">
                                      <path
                                        d="M5 13l4 4L19 7"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                      {selectionStatus?.label || t('selectedLabel')}
                                  </AddedText>
                                ) : (
                                  <PrimaryActionButton
                                    onClick={() => handleAddSource(pulse)}
                                  >
                                    <Icon
                                      name={ICON_SET.add}
                                      size={14}
                                      color="#ffffff"
                                    />
                                        {t('add')}
                                  </PrimaryActionButton>
                                )}
                              </DataCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <EmptyState>
                            <EmptyTitle>{t('noPulseSurveys')}</EmptyTitle>
                          <EmptySubtitle>
                              {t('noPulseSurveysDescription')}
                          </EmptySubtitle>
                        </EmptyState>
                      )}
                    </TableBody>
                    <PaginationComponent
                      totalPages={pulsePageData.totalPages}
                      currentPage={pulsePageData.currentPage}
                      onPageChange={setCurrentPage}
                      totalItems={pulsePageData.totalItems}
                      itemsPerPage={pulsePageData.itemsPerPage}
                      t={t}
                    />
                  </TableContainer>
                </>
              )}
              {activeTab === "selected" && (
                <>
                  <SearchContainer>
                    <SearchInput
                      type="text"
                      placeholder={t('searchSelectedSources')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ flex: "1 1 auto" }}
                    />
                    <ActionButton
                      variant="secondary"
                      onClick={handleClear}
                      disabled={
                        totalSelectedCount === 0 &&
                        !hasCustomDataset &&
                        !hasSurveyData
                      }
                      style={{ flex: "0 0 120px" }}
                    >
                      {t('clear')}
                    </ActionButton>
                  </SearchContainer>
                  <TableContainer>
                    <TableHeader>
                      <HeaderCell width="45%">{t('dataSource')}</HeaderCell>
                      <HeaderCell width="25%">{t('type')}</HeaderCell>
                      <HeaderCell width="15%">{t('updated')}</HeaderCell>
                      <HeaderCell width="15%">{t('actions')}</HeaderCell>
                    </TableHeader>
                    <TableBody>
                      {displaySelectedSources.length > 0 ? (
                        displaySelectedSources
                          .filter((source) => {
                            if (!searchTerm.trim()) return true;
                            const primary = resolveSourcePrimaryTitle(source, t);
                            const secondary =
                              resolveSourceSecondaryLabel(source, t);
                            const haystack = [
                              primary,
                              secondary,
                              source.type,
                            ]
                              .filter(Boolean)
                              .join(" ")
                              .toLowerCase();
                            return haystack.includes(searchTerm.toLowerCase());
                          })
                          .map((source, index) => {
                            const displayTitle =
                              buildSelectedDisplayTitle(source, t);
                            const lastUpdated =
                              resolveSourceLastUpdated(source, t);
                            const isCustomDataset = Boolean(
                              source.isCustomDataset
                            );
                            const isFromDrawer = source?.metadata?.sourceLocation === 'drawer';
                            const hasSelectionMetadata = Boolean(
                              source?.metadata?.selectionType
                            );
                            // Drawer-loaded surveys should always be removable
                            // Only disable removal for analytics-loaded surveys
                            const isDefaultSurveySelection =
                              source.type === "survey" &&
                              !isFromDrawer &&
                              (isCustomDataset ||
                                source.status === 22 ||
                                hasSelectionMetadata);
                            const showDisabledRemove =
                              !isFromDrawer && (isCustomDataset || isDefaultSurveySelection);
                            const badgeInfo = resolveSelectedBadge(source, t);

                            return (
                              <TableRow
                                key={`selected-${source.type}-${source.id}-${index}`}
                                isSelected
                              >
                                <DataCell width="45%">
                                  <DataSourceName>{displayTitle}</DataSourceName>
                                </DataCell>
                                <DataCell width="25%">
                                  {badgeInfo ? (
                                    <DataBadge color={badgeInfo.color}>
                                      {badgeInfo.label}
                                    </DataBadge>
                                  ) : (
                                    <DataValue>
                                        {source.type ? source.type : t('unknown')}
                                    </DataValue>
                                  )}
                                </DataCell>
                                <DataCell width="15%">
                                  <DataValue>{lastUpdated}</DataValue>
                                </DataCell>
                                <DataCell width="15%">
                                  {showDisabledRemove ? (
                                    <DisabledActionWrapper
                                      title={t('defaultSurveyLockedTooltip')}
                                    >
                                      <DisabledIconButton>
                                        <Icon
                                          name={ICON_SET.delete}
                                          size={14}
                                          color={colors.neutral[600]}
                                        />
                                      </DisabledIconButton>
                                    </DisabledActionWrapper>
                                  ) : (
                                    <PrimaryActionButton
                                      variant="danger"
                                      iconOnly
                                      onClick={() => handleRemoveSelected(source)}
                                    >
                                      <Icon
                                        name={ICON_SET.delete}
                                        size={14}
                                      />
                                    </PrimaryActionButton>
                                  )}
                                </DataCell>
                              </TableRow>
                            );
                          })
                      ) : (
                        <EmptyState>
                            <EmptyTitle>{t('noSelectedSurveyData')}</EmptyTitle>
                          <EmptySubtitle>
                              {t('noSelectedSurveyDataDescription')}
                          </EmptySubtitle>
                        </EmptyState>
                      )}
                    </TableBody>
                  </TableContainer>
                </>
              )}
              {activeTab === "filters" && (
                <>
                  <FilterTabContent
                    enableFilters={filtersEnabled}
                    dataSourceConfig={dataSourceConfig}
                    t={t}
                  />
                </>
              )}
            </TabContent>
          </ModalBody>
          <ModalFooter>
            <div>
              {(totalSelectedCount > 0 || hasCustomDataset || hasSurveyData) && (
                <Text
                  typographyStyle={typography.smallText.regular}
                  color={colors.text.gray}
                >
                  {hasCustomDataset && baseSelectedCount === 0
                    ? t('customDatasetLoaded')
                    : `${totalSelectedCount} ${totalSelectedCount !== 1 ? t('sourcesSelectedPlural') : t('sourcesSelected')
                    }`}
                  {hasCustomDataset &&
                    baseSelectedCount > 0 &&
                    t('plusCustomDataset')}
                  {hasSurveyData && !hasCustomDataset && t('includesSurveyData')}
                </Text>
              )}
            </div>
            <ButtonGroup>
              {showingForMissingData ? (
                <ActionButton
                  variant="primary"
                  onClick={onApplyAndStartChat}
                  disabled={
                    totalSelectedCount === 0 && !hasCustomDataset && !hasSurveyData
                  }
                >
                  {t('applyAndStartChat')}
                </ActionButton>
              ) : (
                <ActionButton variant="primary" onClick={handleApply}>
                    {t('applyToChat')}
                </ActionButton>
              )}
            </ButtonGroup>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    </>
  );
};

DataSourceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dataSourceConfig: PropTypes.array,
  selectedSources: PropTypes.array,
  setSourceType: PropTypes.func,
  chatStarted: PropTypes.bool,
  uploadPending: PropTypes.bool,
  fileClearCb: PropTypes.func,
  feedbackLength: PropTypes.number,
  handleRemoveAllFactors: PropTypes.func,
  handleRemoveAllOutcomes: PropTypes.func,
  handleRemoveAllCustomQuestions: PropTypes.func,
  enableFilters: PropTypes.bool,
  onClear: PropTypes.func,
  onApply: PropTypes.func,
  showingForMissingData: PropTypes.bool,
  onApplyAndStartChat: PropTypes.func,
};

DataSourceModal.defaultProps = {
  dataSourceConfig: [],
  selectedSources: [],
  chatStarted: false,
  uploadPending: false,
  feedbackLength: 0,
  enableFilters: false,
  showingForMissingData: false,
};

// Styled components for FilterTabContent - Updated to match NewPermLayout style
const FilterTabContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  font-family: "Poppins", sans-serif;
`;

const FilterTabHeader = styled.div`
  padding: 12px 24px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  background: white;
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ClearAllButton = styled.button`
  color: #F15062;
  font-weight: 600;
  border: 1px solid #F2F2F2;
  background: white;
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 5px;
  padding: 6px 12px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #FFF5F6;
    border-color: #F15062;
  }
`;

const FilterTabMain = styled.div`
  display: flex;
  height: 400px;
  overflow: hidden;
  background: white;
`;

const FilterSidebar = styled.div`
  width: 240px;
  min-width: 240px;
  padding: 20px;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #f8f9fa;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const FilterSidebarHeader = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #414141;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const FilterCategoryItem = styled.div`
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #7e7e7e;
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    color: #476dfa;
    background: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(71, 109, 250, 0.1);
  }
`;

const FilterMainContent = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background: white;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const FilterSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterSectionTitle = styled.h3`
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #414141;
  margin: 0 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid #f0f0f0;
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  cursor: pointer;
  user-select: none;
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #414141;
  border-bottom: 2px solid #f0f0f0;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  
  &:hover {
    color: #476dfa;
  }
  
  span {
    flex: 1;
  }
`;

const CaretIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${props => props.isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  color: #7e7e7e;
  font-size: 14px;
  width: 24px;
  height: 24px;
`;

const FilterOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const FilterOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${(p) => (p.selected ? "#476dfa" : "white")};
  color: ${(p) => (p.selected ? "white" : "#414141")};
  border: 1px solid ${(p) => (p.selected ? "#476dfa" : "#e0e0e0")};
  border-radius: 8px;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${(p) => (p.selected ? "#3d5ce6" : "#f8f9fa")};
    border-color: ${(p) => (p.selected ? "#3d5ce6" : "#476dfa")};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(71, 109, 250, 0.15);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FilterCheckIcon = styled.img`
  width: 16px;
  height: 16px;
  filter: ${(p) => (p.selected ? "brightness(0) invert(1)" : "none")};
`;

const FilterTabEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;
  color: #7e7e7e;
  font-family: "Poppins", sans-serif;
  background: white;

  h3 {
    font-family: "Poppins", sans-serif;
    font-weight: 600;
    font-size: 20px;
    color: #414141;
    margin: 0 0 8px 0;
  }

  p {
    font-family: "Poppins", sans-serif;
    font-weight: 400;
    font-size: 16px;
    color: #7e7e7e;
    margin: 0;
    line-height: 1.5;
  }
`;

export default DataSourceModal;
